import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, EmptyState, StatusPill } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import type { CommonFeature, CommonFeatureType, CommonFeatureStatus } from '../types/catalog';

/**
 * #336 — module codes that have a live demo panel in /showcase
 * Based on getDemoPanel() logic in ModuleCatalogTree: modules with non-null demoEndpoint.
 */
const DEMO_MODULE_CODES = new Set([
  'C001', // error/exception demo
  'C003', // validate
  'C004', // utility
  'C007', // cache
  'C008', // event
  'C009', // file (demo available via FilePanel)
  'C012', // crypto
  'C013', // mask
  'C039', // jpa
  'C078', // excel
]);

interface CommonFeaturesTabProps {
  features: CommonFeature[];
}

type SortField = 'seq' | 'priority' | 'module';
type SortDir = 'asc' | 'desc';

const STATUS_TONE: Record<CommonFeatureStatus, 'success' | 'warning' | 'info' | 'neutral'> = {
  DONE:        'success',
  SPEC:        'warning',
  IN_PROGRESS: 'info',
  SUPERSEDED:  'info',     // 대체됨 — 다른 구현으로 대체(미완 아님)
  BLOCKED:     'warning',  // 보류 — 외부 인프라/계정 제약으로 진행 불가
  PENDING:     'neutral',
};

const PRIORITY_TONE: Record<string, 'danger' | 'warning' | 'info' | 'neutral'> = {
  P1: 'danger',
  P2: 'warning',
  P3: 'info',
  P4: 'neutral',
  P5: 'neutral',
};

export function CommonFeaturesTab({ features }: CommonFeaturesTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // UAT 2026-05-20 r2 #462: 관련 biz / biz code 컬럼 제거 — 능력 카탈로그(/common-showcase)와 중복.
  // 비고(note) 컬럼이 점유 영역을 흡수하도록 width 미지정(flex) + 줄바꿈 허용.
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<CommonFeatureType | ''>('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStatus, setFilterStatus] = useState<CommonFeatureStatus | ''>('');
  const [sortField, setSortField] = useState<SortField>('seq');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const STATUS_LABEL: Record<CommonFeatureStatus, string> = {
    DONE:        t('index.features.status.done'),
    SPEC:        t('index.features.status.spec'),
    IN_PROGRESS: t('index.features.status.inProgress'),
    SUPERSEDED:  t('index.features.status.superseded'),
    BLOCKED:     t('index.features.status.blocked'),
    PENDING:     t('index.features.status.pending'),
  };

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDir('asc');
      }
    },
    [sortField]
  );

  const filtered = useMemo(() => {
    let result = [...features];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (f) =>
          f.name.toLowerCase().includes(q) ||
          f.code.toLowerCase().includes(q) ||
          f.module.toLowerCase().includes(q) ||
          f.moduleDescription.toLowerCase().includes(q) ||
          (f.featureDescription ?? '').toLowerCase().includes(q)
      );
    }
    if (filterType) result = result.filter((f) => f.type === filterType);
    if (filterPriority) result = result.filter((f) => f.priority === filterPriority);
    if (filterStatus) result = result.filter((f) => f.status === filterStatus);

    result.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'seq') cmp = a.seq - b.seq;
      else if (sortField === 'priority') cmp = a.priority.localeCompare(b.priority);
      else if (sortField === 'module') cmp = a.module.localeCompare(b.module);
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [features, search, filterType, filterPriority, filterStatus, sortField, sortDir]);

  const columns: DataTableColumn<CommonFeature>[] = [
    {
      key: 'seq',
      header: t('index.features.col.seq'),
      width: '60px',
      render: (row) => (
        <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
          {row.seq}
        </span>
      ),
    },
    {
      key: 'type',
      header: t('index.features.col.type'),
      width: '90px',
      render: (row) => (
        <span
          style={{
            display: 'inline-block',
            padding: '2px var(--spacing-2)',
            fontSize: 'var(--font-size-xs)',
            borderRadius: 'var(--radius-full)',
            border: '1px solid',
            borderColor:
              row.type === 'common'
                ? 'var(--color-brand-primary)'
                : 'var(--color-info)',
            color:
              row.type === 'common'
                ? 'var(--color-brand-primary)'
                : 'var(--color-info)',
            background: 'transparent',
            fontWeight: 600,
          }}
        >
          {row.type === 'common' ? 'common' : 'gateway'}
        </span>
      ),
    },
    {
      key: 'code',
      header: t('index.features.col.code'),
      width: '80px',
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {row.code}
        </span>
      ),
    },
    {
      key: 'priority',
      header: t('index.features.col.priority'),
      width: '90px',
      render: (row) => (
        <StatusPill tone={PRIORITY_TONE[row.priority] ?? 'neutral'}>
          {row.priority}
        </StatusPill>
      ),
    },
    {
      key: 'module',
      header: t('index.features.col.module'),
      width: '140px',
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-primary)',
          }}
        >
          {row.module}
        </span>
      ),
    },
    {
      key: 'featureDescription',
      header: t('index.features.col.featureDescription'),
      width: '260px',
      render: (row) => {
        // featureDescription 이 비어있으면 moduleDescription 으로 fallback (회귀 안전망)
        const text = row.featureDescription || row.moduleDescription;
        return (
          <span
            title={text}
            style={{
              display: 'block',
              maxWidth: '260px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      key: 'name',
      header: t('index.features.col.name'),
      width: '180px',
      render: (row) => (
        <span
          style={{
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
          }}
        >
          {row.name}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('index.features.col.status'),
      width: '90px',
      render: (row) => (
        <StatusPill tone={STATUS_TONE[row.status]}>
          {STATUS_LABEL[row.status]}
        </StatusPill>
      ),
    },
    {
      key: 'note',
      header: t('index.features.col.note'),
      render: (row) => {
        /* R11-U-002: WaveTooltipNote 제거 — 'Wave N' 등 내부 개발 마일스톤은
           일반 사용자 화면에서 노출하지 않는다.
           UAT 2026-05-20 r2 #462: 관련 biz/biz code 컬럼 흡수 — 줄바꿈 허용 + minWidth 확장. */
        if (!row.note) {
          return <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>—</span>;
        }
        const isWaveOnly = /^Wave\s*\d+\s*$/i.test(row.note.trim());
        if (isWaveOnly) {
          return <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>—</span>;
        }
        return (
          <span
            title={row.note}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              lineHeight: 1.5,
              minWidth: 320,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {row.note}
          </span>
        );
      },
    },
    {
      /* #336 — Demo action column */
      key: 'action' as keyof CommonFeature,
      header: t('index.features.col.action'),
      width: '110px',
      render: (row) => {
        const hasDemo = DEMO_MODULE_CODES.has(row.module) || DEMO_MODULE_CODES.has(row.code);
        if (!hasDemo) {
          return (
            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
              {t('index.features.action.noDemo')}
            </span>
          );
        }
        return (
          <button
            type="button"
            aria-label={t('index.features.action.demo') + ' ' + row.code}
            onClick={() => {
              navigate('/showcase', { state: { scrollTo: row.code } });
            }}
            style={{
              padding: '2px var(--spacing-2)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-xs)',
              cursor: 'pointer',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {t('index.features.action.demo')} →
          </button>
        );
      },
    },
  ];

  const sortIndicator = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const sortFieldLabel: Record<SortField, string> = {
    seq:      t('index.features.sort.seq'),
    priority: t('index.features.sort.priority'),
    module:   t('index.features.sort.module'),
  };

  return (
    <div>
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          marginBottom: 'var(--spacing-4)',
          alignItems: 'center',
        }}
      >
        {/* Search */}
        <input
          type="search"
          placeholder={t('index.features.search.placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('index.features.search.aria')}
          style={{
            flex: '1 1 200px',
            minWidth: '160px',
            height: '36px',
            padding: '0 var(--spacing-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-sm)',
            outline: 'none',
          }}
        />

        {/* Type filter */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as CommonFeatureType | '')}
          aria-label={t('index.features.filter.type')}
          style={{
            height: '36px',
            padding: '0 var(--spacing-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-sm)',
            cursor: 'pointer',
          }}
        >
          <option value="">{t('index.features.filter.typeAll')}</option>
          <option value="common">common</option>
          <option value="gateway">gateway</option>
        </select>

        {/* Priority filter */}
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          aria-label={t('index.features.filter.priority')}
          style={{
            height: '36px',
            padding: '0 var(--spacing-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-sm)',
            cursor: 'pointer',
          }}
        >
          <option value="">{t('index.features.filter.priorityAll')}</option>
          {['P1', 'P2', 'P3', 'P4', 'P5'].map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        {/* Status filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as CommonFeatureStatus | '')}
          aria-label={t('index.features.filter.status')}
          style={{
            height: '36px',
            padding: '0 var(--spacing-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-sm)',
            cursor: 'pointer',
          }}
        >
          <option value="">{t('index.features.filter.statusAll')}</option>
          <option value="DONE">{t('index.features.status.done')}</option>
          <option value="IN_PROGRESS">{t('index.features.status.inProgress')}</option>
          <option value="SPEC">{t('index.features.status.spec')}</option>
          <option value="SUPERSEDED">{t('index.features.status.superseded')}</option>
          <option value="BLOCKED">{t('index.features.status.blocked')}</option>
          <option value="PENDING">{t('index.features.status.pending')}</option>
        </select>

        {/* Sort buttons */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginLeft: 'auto' }}>
          {(['seq', 'priority', 'module'] as SortField[]).map((field) => (
            <button
              key={field}
              type="button"
              onClick={() => handleSort(field)}
              aria-label={t('index.features.sort.aria', { field })}
              style={{
                height: '36px',
                padding: '0 var(--spacing-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-surface-border)',
                background:
                  sortField === field
                    ? 'var(--color-brand-primary)'
                    : 'var(--color-surface-muted)',
                color:
                  sortField === field
                    ? 'var(--color-text-inverse)'
                    : 'var(--color-text-secondary)',
                fontSize: 'var(--font-size-xs)',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
              }}
            >
              {sortFieldLabel[field]}
              {sortIndicator(field)}
            </button>
          ))}
        </div>
      </div>

      {/* Count badge */}
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          marginBottom: 'var(--spacing-2)',
        }}
      >
        {t('index.features.count', { filtered: filtered.length, total: features.length })}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        /* R9-F-004: 검색 결과 0건 EmptyState 에 [검색 초기화] CTA 추가 */
        <EmptyState
          title={t('index.features.empty.title')}
          description={t('index.features.empty.description')}
          action={
            (search || filterType || filterPriority || filterStatus) ? (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setFilterType('');
                  setFilterPriority('');
                  setFilterStatus('');
                }}
                aria-label={t('index.features.empty.resetAria')}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                }}
              >
                {t('index.features.empty.reset')}
              </button>
            ) : undefined
          }
        />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(row) => row.seq}
          />
        </div>
      )}
    </div>
  );
}
